'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { Control } from 'react-hook-form';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Checkbox from '@/components/Form/Checkbox';
import Input from '@/components/Form/FormInput';
import PrescriptionForm from '@/components/PrescriptionFormV2/PrescriptionFormExtra';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { EPrescriptionType } from '@/utils/enums/goods';
import { PRESCRIPTION_NAME_FORMAT_INPUT, PRESCRIPTION_NAME_PATTERN } from '@/utils/constants';
import { PrescriptionParamType } from '@/app/account/my-prescriptions/add-prescription/components/learn-prescription-dialog';
import useResponsive from '@/hooks/useResponsive';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { env } from '@/utils/env';
import useTrackEditPrescription from '../services/useTrackEditPrescription';
import { useCustomerService } from '@/hooks/useCustomerService';
import { useStore } from '@/providers/app-store-provider';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';

export interface FillOutPrescriptionMainProps {
  prescriptionType: EPrescriptionType;
  prescriptionOptions: IPrescriptionOptions;
  isFromScan?: boolean;
  uploadedS3Url?: string;
  control: Control<any>;
  trigger: any; // UseFormTrigger from react-hook-form
  saveMyPres: boolean;
  lensGoodsItem: any;
  /** 当前步骤是否激活（用于清空验证和重置状态） */
  isStepActive?: boolean;
  onPrescriptionSelectionOpen: (key: string) => void;
  onSaveMyPresChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLearnPrescriptionClick: (tab?: PrescriptionParamType) => void;
  onModifyIsFromScan: (isFromScan: boolean) => void;
  /** 清空表单错误的函数 */
  clearErrors?: (name?: any) => void;
}

const FillOutPrescriptionMain: React.FC<FillOutPrescriptionMainProps> = ({
  prescriptionType,
  prescriptionOptions,
  control,
  trigger,
  isFromScan = false,
  uploadedS3Url,
  saveMyPres,
  lensGoodsItem,
  isStepActive = false,
  onPrescriptionSelectionOpen,
  onSaveMyPresChange,
  onLearnPrescriptionClick,
  clearErrors,
  onModifyIsFromScan,
}) => {
  const t = useTranslations('lens-process');
  const { isPc } = useResponsive();

  const { trackFillOutPrescriptionMainView, trackIntroduceParam, trackFillPrescriptionCheckClick } =
    useTrackEditPrescription();
  const inAppH5 = useStore((state) => state.inAppH5);
  const { openCustomerService } = useCustomerService({ inAppH5 });
  const [prescriptionFormKey, setPrescriptionFormKey] = useState(0);
  const { selectedPrescription } = useLensProcessStore();

  // 当步骤不激活时，清空验证错误并重置 PrescriptionForm 状态
  useEffect(() => {
    if (!isStepActive) {
      // 清空表单验证错误
      clearErrors?.('prescription');
      onModifyIsFromScan(false);
      // 通过改变 key 来重新渲染 PrescriptionForm 组件，这样会重置其内部状态
      // 包括 add prism 和 2 pd 的选中状态
      setPrescriptionFormKey((prev) => prev + 1);
    } else {
      trackFillOutPrescriptionMainView();
    }
  }, [isStepActive, clearErrors]);

  return (
    <div className="h-full w-full">
      <div className="max-xl:px-[14px] flex justify-between items-center xl:mb-[16px]">
        <div className={`xl:!pb-[0px] ${stepCommonClassName.stepTitle} !pl-0 `}>
          {t('enterYourPrescription')}
        </div>
        <div
          className="sub-title gap-[4px] items-center hidden xl:flex cursor-pointer  hover:text-primary-purple group"
          onClick={() => {
            onLearnPrescriptionClick();
            trackIntroduceParam('PrescriptionParam');
          }}
        >
          <QuestionResponsive
            className={`${stepCommonClassName.questionIcon} group-hover:text-primary-purple`}
          />
          <span className=" text-12 underline">{t('learnHowToReadYourPrescription')}</span>
        </div>
      </div>
      {isFromScan && (
        <div className={`flex-1 mb-[24px] font-12 f12 ${isPc ? '' : 'px-[14px]'}`}>
          <span>{t('pleaseConfirmThatThePrescriptionMatchesTheFollowingInformation')}</span>
          <PhotoProvider key={uploadedS3Url} maskClassName="!bg-black/50" bannerVisible={false}>
            <PhotoView src={env.NEXT_PUBLIC_S3_URL + uploadedS3Url}>
              <span className={`underline cursor-pointer ${isPc ? 'ml-[4px]' : 'block mt-[12px]'}`}>
                {t('viewUploadedPrescription')}
              </span>
            </PhotoView>
          </PhotoProvider>
        </div>
      )}
      <div className="section-content px-[14px] py-[20px] xl:py-0 bg-white rounded-t-[12px] xl:px-0 xl:bg-transparent">
        <div className="sub-title flex gap-[4px] mb-12 items-center xl:hidden">
          <span className="font-bs text-16 ">{t('prescription')}</span>
          <QuestionResponsive
            className={stepCommonClassName.questionIcon}
            onClick={() => {
              onLearnPrescriptionClick();
              trackIntroduceParam('PrescriptionParam');
            }}
          />
        </div>
        <div
          className={`p-12 bg-[#fff9d7] rounded-[6px] text-12 ${isPc ? 'mb-16' : 'flex flex-col mb-[12px]'}`}
        >
          <span className="mr-6">{t('sphMinus')}</span>
          <span>{t('sphPlus')}</span>
        </div>
        <PrescriptionForm
          key={prescriptionFormKey}
          className=""
          // * 放弃apiPrescriptionType, 如果使用getApiPrescriptionType, 会导致老花处方镜识别为老花成品镜
          // * 这两种老花镜类型实际上不一样，在sph的范围限制上是不一样的
          prescriptionType={prescriptionType}
          pd_min={lensGoodsItem?.product?.pd_min || '54'}
          pd_max={lensGoodsItem?.product?.pd_max || '78'}
          prescriptionOptions={prescriptionOptions}
          name="prescription"
          control={control}
          onOpen={onPrescriptionSelectionOpen}
          onClickPdHelp={() => {
            onLearnPrescriptionClick('pd');
            trackIntroduceParam('PdHelp');
          }}
          onClickPrismHelp={() => {
            onLearnPrescriptionClick('prism');
            trackIntroduceParam('PrismParam');
          }}
          onPdCheckChange={(checked) => {
            trackFillPrescriptionCheckClick('2 PD Numbers', checked ? 'Checked' : 'Cancel');
          }}
          onAddPrism={(checked) => {
            trackFillPrescriptionCheckClick('Add Prism', checked ? 'Checked' : 'Cancel');
          }}
        />

        {/* 保存处方区域 */}
        <div className=" border-[#d0d4db]">
          <div className="flex items-center">
            <Checkbox
              label={selectedPrescription?.id ? t('updateMyPrescription') : t('saveMyPrescription')}
              checked={saveMyPres}
              isThinner
              onChange={(e) => {
                onSaveMyPresChange(e);
                trackFillPrescriptionCheckClick(
                  selectedPrescription?.id ? 'Update My Prescription' : 'Save My Prescription',
                  e.target.checked ? 'Checked' : 'Cancel',
                );
              }}
              sx={{ '& .MuiTypography-root': { fontSize: '14px' } }}
            />
          </div>
          {saveMyPres && (
            <div className="pb-[10px] xl:w-[374px]">
              <Input
                size="small"
                placeholder={t('prescriptionNamePlaceholder')}
                name="prescriptionName"
                control={control}
                formatInput={PRESCRIPTION_NAME_FORMAT_INPUT}
                formItemClassName="mb-0"
                onBlur={() => {
                  trigger('prescriptionName');
                }}
                rules={{
                  required: t('prescriptionNameRequired'),
                  pattern: {
                    value: PRESCRIPTION_NAME_PATTERN,
                    message: undefined,
                  },
                }}
                slotProps={{
                  htmlInput: {
                    maxLength: 20,
                  },
                }}
                className="w-full"
              />
            </div>
          )}
        </div>
        <div className="max-xl:px-[14px] py-[24px] text-center xl:pt-[56px] xl:pb-[20px] xl:text-left">
          <p className="text-[14px] mb-[12px] hidden xl:block">
            {t('unableToMakeSureYourPrescription')}
          </p>
          <div
            className="f12 underline cursor-pointer xl:text-primary-purple inline-block"
            onClick={() => {
              openCustomerService();
              trackIntroduceParam('Message');
            }}
          >
            {t('leaveAMessage')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FillOutPrescriptionMain;
